<template>
	<div class="chat-container" v-show="visible">
		<div class="chat-header">
			<span>Chat</span>
			<svg @click="$emit('close')" style="width: 20px; height: 20px" viewBox="0 0 24 24">
				<path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
			</svg>
		</div>
		<div @click="hideDropdowns" class="chat-messages" ref="chatMessages">
			<div class="chat-messages-info" :class="alreadyOpen ? 'alreadyOpen' : visible ? 'show' : 'hide'">
				{{ chatInfoText }}
			</div>
			<div v-for="(message, index) in messages" :key="message.message + '_' + index" class="msg" :class="[isMyMessage(message) ? 'right-msg' : 'left-msg', { 'private-msg': message.private }]">
				<div class="msg-bubble" :style="getBubbleMinWidth(message.message.name + ' ' + message.message.time)">
					<div class="private-badge" v-show="message.private">
						<svg style="width: 24px; height: 24px" viewBox="0 0 24 24">
							<path fill="currentColor" d="M23 3V2.5C23 1.1 21.9 0 20.5 0S18 1.1 18 2.5V3C17.5 3 17 3.5 17 4V8C17 8.5 17.5 9 18 9H23C23.5 9 24 8.5 24 8V4C24 3.5 23.5 3 23 3M22 3H19V2.5C19 1.7 19.7 1 20.5 1S22 1.7 22 2.5V3M22 11V16C22 17.1 21.1 18 20 18H6L2 22V4C2 2.9 2.9 2 4 2H15V4H4V17.2L5.2 16H20V11H22Z" />
						</svg>
					</div>
					<div class="msg-container">
						<div class="msg-info">
							<div class="msg-info-name" v-text="message.message.name" />
							<div class="msg-info-time" v-text="message.message.time" />
						</div>
						<div class="msg-text" v-html="message.message.text" />
					</div>
				</div>
			</div>
		</div>

		<div class="chat-input">
			<Emoji v-if="showEmoji" searchLabel="Search emoji here" @click="handleEmojiClick" />
			<span class="chat-button-wrapper" :class="{ disabled: participants.length === 0 }" @click="togglePrivateMessageMode" @keyup="handleUlFocus">
				<button class="chat-button" :disabled="participants.length === 0">
					<svg style="cursor: pointer; width: 24px; height: 24px" viewBox="0 0 24 24">
						<path :fill="textMessage.startsWith('@') ? 'rgba(0, 174, 255, 1)' : 'white'" d="M23 3V2.5C23 1.1 21.9 0 20.5 0S18 1.1 18 2.5V3C17.5 3 17 3.5 17 4V8C17 8.5 17.5 9 18 9H23C23.5 9 24 8.5 24 8V4C24 3.5 23.5 3 23 3M22 3H19V2.5C19 1.7 19.7 1 20.5 1S22 1.7 22 2.5V3M22 11V16C22 17.1 21.1 18 20 18H6L2 22V4C2 2.9 2.9 2 4 2H15V4H4V17.2L5.2 16H20V11H22Z" />
					</svg>
				</button>
				<span class="chat-tooltip privateMessage">{{ $t('send_private_message') }}</span>
			</span>

			<div style="position: relative; display: flex; flex: 1">
				<input
					autocomplete="do-not-autofill"
					ref="chatInput"
					@keydown="handleInputKeydown"
					@keyup="handleInputKeyup"
					@keyup.enter="sendMessage"
					@focus="hideEmoji"
					class="chat"
					v-model="textMessage"
					type="text"
					:placeholder="messagePlaceholder"
				/>
				<div class="suggestion" v-show="showParticipantSuggestions && filteredParticipants.length > 0" :style="'top: -' + suggestionBoxHeight + 'px'" @keyup="handleUlFocus">
					<div>Messaggio privato</div>
					<ul ref="participantsList" @keyup="handleUlFocus">
						<li @keyup="handleUlFocus" @keyup.prevent.enter="selectParticipant(participant.name)" tabindex="-1" v-for="(participant, index) in filteredParticipants" :key="index" @click="selectParticipant(participant.name)">
							@{{ participant.name }}
						</li>
					</ul>
				</div>
			</div>
			<button class="send" :disabled="textMessage === ''" @click="sendMessage">
				<svg style="width: 24px; height: 24px" viewBox="0 0 24 24">
					<path fill="currentColor" d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
				</svg>
			</button>
			<span class="chat-button-wrapper" @click="toggleEmoji">
				<button class="chat-button">
					<svg style="cursor: pointer; width: 24px; height: 24px" viewBox="0 0 24 24">
						<path :fill="showEmoji ? 'rgba(0, 174, 255, 1)' : 'white'" d="M12,2C6.47,2 2,6.47 2,12C2,17.53 6.47,22 12,22A10,10 0 0,0 22,12C22,6.47 17.5,2 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M13,9.94L14.06,11L15.12,9.94L16.18,11L17.24,9.94L15.12,7.82L13,9.94M8.88,9.94L9.94,11L11,9.94L8.88,7.82L6.76,9.94L7.82,11L8.88,9.94M12,17.5C14.33,17.5 16.31,16.04 17.11,14H6.89C7.69,16.04 9.67,17.5 12,17.5Z" />
					</svg>
				</button>
				<span class="chat-tooltip emojy">{{ $t('use_emoji') }}</span>
			</span>
		</div>
	</div>
</template>

<script>
import { VueChatEmoji } from 'vue-chat-emoji'

export default {
	name: 'Chat',
	components: {
		Emoji: VueChatEmoji
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		messages: {
			type: Array,
			required: true
		},
		participants: {
			type: Array,
			required: true
		},
		currentUserId: {
			type: String,
			required: true
		},
		alreadyOpen: {
			type: Boolean,
			default: false
		},
		chatInfoText: {
			type: String,
			default: 'Benvenuto nella chat!'
		},
		messagePlaceholder: {
			type: String,
			default: 'Scrivi un messaggio...'
		}
	},
	data() {
		return {
			textMessage: '',
			showEmoji: false,
			showParticipantSuggestions: false
		}
	},
	computed: {
		filteredParticipants() {
			const searchString = this.textMessage.slice(1)
			let filtered = this.participants.filter(p => !p.isLocal)

			if (searchString !== '') {
				filtered = filtered.filter(p =>
					p.name && p.name.toLowerCase().includes(searchString.toLowerCase())
				)
			}

			return filtered
		},
		suggestionBoxHeight() {
			if (this.filteredParticipants.length > 0) {
				let calc = this.filteredParticipants.length * 25
				calc = calc + 28
				return calc >= 135 ? 135 : calc
			}
			return 0
		}
	},
	watch: {
		visible(newVal) {
			if (newVal) {
				this.$nextTick(() => {
					this.scrollToBottom()
				})
			}
		},
		messages() {
			if (this.visible) {
				this.$nextTick(() => {
					this.scrollToBottom()
				})
			}
		}
	},
	methods: {
		isMyMessage(message) {
			return message.message.id === this.currentUserId
		},
		getBubbleMinWidth(text) {
			const canvas = document.createElement('canvas')
			const context = canvas.getContext('2d')
			const metrics = context?.measureText(text)
			return {
				minWidth: metrics ? Math.ceil(metrics.width) + 'px' : 'auto'
			}
		},
		hideEmoji() {
			this.showEmoji = false
		},
		hideDropdowns() {
			this.showEmoji = false
			this.showParticipantSuggestions = false
		},
		handleEmojiClick(emoji) {
			this.textMessage += ' ' + emoji.emoji + ' '
		},
		toggleEmoji() {
			this.showParticipantSuggestions = false
			this.showEmoji = !this.showEmoji
		},
		togglePrivateMessageMode() {
			this.showParticipantSuggestions = !this.showParticipantSuggestions
			this.hideEmoji()
			if (this.showParticipantSuggestions && this.textMessage === '') {
				this.textMessage = '@'
				this.focusInput()
			}
		},
		handleInputKeydown(e) {
			if (e.key === 'Backspace' && this.textMessage.startsWith('@')) {
				const input = e.target
				const cursorPosition = input.selectionStart

				console.log('Debug:', {
					text: this.textMessage,
					cursorPosition: cursorPosition,
					char: this.textMessage.charAt(cursorPosition - 1)
				})

				// Cerca il primo spazio per identificare dove finisce il tag
				const spaceIndex = this.textMessage.indexOf(' ')

				if (spaceIndex > 1) { // Tag completo trovato (@nome )
					// Se il cursore è alla fine del nome (prima dello spazio) o sullo spazio
					if (cursorPosition === spaceIndex || cursorPosition === spaceIndex + 1) {
						console.log('Removing tag!')
						e.preventDefault()
						this.textMessage = ''
						this.showParticipantSuggestions = false
					}
				}
			}
		},
		handleInputKeyup(e) {
			this.hideEmoji()

			if ((e.keyCode === 38 || e.keyCode === 40) && this.showParticipantSuggestions) {
				const ul = this.$refs.participantsList
				if (ul && ul.children.length > 0) {
					const firstItem = ul.children[0]
					firstItem.focus()
				}
			} else {
				if (e.target && (e.target).selectionStart === 1) {
					this.showParticipantSuggestions = true
				}

				if (this.textMessage.charAt(0) === '@') {
					const split = this.textMessage.split(' ')
					if (split.length === 1) {
						const searchString = this.textMessage.slice(1)
						const found = this.participants.find(p =>
							!p.isLocal && p.name && p.name.toLowerCase().startsWith(searchString.toLowerCase())
						)
						this.showParticipantSuggestions = found ? found.name !== searchString : false
					} else {
						this.showParticipantSuggestions = false
					}
				} else {
					this.showParticipantSuggestions = false
				}
			}
		},
		handleUlFocus(e) {
			if ((e.keyCode === 38 || e.keyCode === 40) && this.showParticipantSuggestions) {
				const ul = this.$refs.participantsList
				if (!ul) return

				let index = null
				for (let i = 0; i < ul.children.length; i++) {
					if (ul.children[i] === document.activeElement) {
						index = i
						break
					}
				}

				if (e.keyCode === 38 && index !== null && index > 0) {
					(ul.children[index - 1]).focus()
				} else if (e.keyCode === 40 && index !== null && index < ul.children.length - 1) {
					(ul.children[index + 1]).focus()
				}
			}
		},
		selectParticipant(name) {
			try {
				this.textMessage = '@' + name.replace(/\s+/g, '_') + ' '
			} catch (error) {
				console.log('Error replacing participant name:', error)
			}
			this.showParticipantSuggestions = false
			this.focusInput()
		},
		focusInput() {
			this.$nextTick(() => {
				const input = this.$refs.chatInput
				if (input) {
					input.focus()
				}
			})
		},
		sendMessage() {
			this.hideEmoji()
			if (this.textMessage === '') {
				return
			}

			if (this.textMessage.charAt(0) === '@') {
				const split = this.textMessage.trim().split(' ')
				if (split.length === 1) {
					return
				}
			}

			this.$emit('send-message', this.textMessage)
			this.textMessage = ''

			this.$nextTick(() => {
				this.scrollToBottom()
			})
		},
		scrollToBottom() {
			const element = this.$refs.chatMessages
			if (element) {
				element.scrollTop = element.scrollHeight
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.chat-container {
	box-shadow: 0 3px 6px rgba(255, 255, 255, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
	background: #0c1e3d;
	overflow: hidden;
	flex-direction: column;
	height: 100%;
	display: flex;
	align-content: center;
	flex-wrap: wrap;
	align-items: center;
	justify-content: center;
	vertical-align: middle;
	flex: 1;
	border-radius: 10px;

	.chat-header {
		background: rgba(116, 171, 226, 0.15);
		width: 100%;
		height: 40px;
		font-size: 14px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		border-bottom: 1px solid hsla(210, 65%, 67%, 0.55);
		color: white;

		span,
		svg {
			cursor: pointer;
			padding: 0 10px;
		}

		svg path {
			fill: white;
		}
	}

	.chat-messages {
		width: 100%;
		flex-direction: column;
		display: flex;
		flex: 1;
		overflow-y: auto;

		.chat-messages-info {
			line-height: 1rem;
			margin: 0.75rem;
			padding: 0.75rem;
			border-radius: 8px;
			color: white;
			opacity: 0;
			transform: scale(0);
			transition: all 0.4s ease-out;
			transition-delay: 0s;
			background: rgba(255, 255, 255, 0.05);
			border: 1px solid rgba(255, 255, 255, 0.1);
			border-radius: 8px;
			font-size: 13px;
			text-align: center;

			&.show {
				transform: scale(1);
				opacity: 1;
				transition: all 0.4s ease-out;
				transition-delay: 0.55s;
			}

			&.alreadyOpen {
				transform: scale(1);
				opacity: 1;
			}

			&.hide {
				transform: scale(0);
				opacity: 0;
				transition: all 0.2s ease-out;
				transition-delay: 0s;
			}
		}

		.msg {
			display: flex;
			margin: 0.25rem 0.75rem;
			flex-direction: column;
			align-items: flex-start;

			&.right-msg {
				align-items: flex-end;

				.msg-bubble {
					background: #3280d3;
					color: white;
					border-radius: 18px 18px 4px 18px;
				}

				.msg-info {
					color: #c9d1d9;
				}
			}

			&.left-msg {
				align-items: flex-start;

				.msg-bubble {
					background: rgba(255, 255, 255, 0.1);
					color: white;
					border-radius: 18px 18px 18px 4px;
				}

				.msg-info {
					color: #8b949e;
				}
			}

			&.private-msg {
				.msg-bubble {
					border: 2px solid #ff6b6b;
					position: relative;
				}

				.private-badge {
					position: absolute;
					top: -8px;
					right: -8px;
					background: #ff6b6b;
					border-radius: 50%;
					width: 24px;
					height: 24px;
					display: flex;
					align-items: center;
					justify-content: center;

					svg {
						width: 12px;
						height: 12px;
					}
				}
			}

			.msg-bubble {
				max-width: 80%;
				padding: 0.5rem 1rem;
				word-wrap: break-word;
				margin: 0.25rem 0;
				position: relative;
			}

			.msg-container {
				width: 100%;
			}

			.msg-info {
				display: flex;
				justify-content: space-between;
				font-size: 0.75rem;
				margin-bottom: 0.25rem;
			}

			.msg-info-name {
				font-weight: 600;
			}

			.msg-info-time {
				opacity: 0.7;
			}

			.msg-text {
				line-height: 1.4;
				font-size: 0.9rem;
			}
		}
	}

	.chat-input {
		background: rgba(116, 171, 226, 0.15);
		position: relative;
		border-top: 1px solid hsla(210, 65%, 67%, 0.55);
		width: 100%;
		display: flex;
		align-items: center;
		padding: 0.5rem;
		gap: 0.5rem;

		.chat-button-wrapper {
			position: relative;
			display: flex;
			align-items: center;

			&.disabled {
				opacity: 0.5;
				pointer-events: none;
			}

			&:hover .chat-tooltip {
				opacity: 1;
				transform: translateY(-100%);
			}

			.chat-button {
				background: transparent;
				border: none;
				cursor: pointer;
				padding: 0.5rem;
				display: flex;
				align-items: center;
				justify-content: center;
				border-radius: 50%;
				transition: background-color 0.2s;

				&:hover {
					background: rgba(255, 255, 255, 0.1);
				}
			}

			.chat-tooltip {
				position: absolute;
				bottom: 100%;
				left: 50%;
				transform: translateX(-50%);
				background: #000;
				color: white;
				padding: 0.25rem 0.5rem;
				border-radius: 4px;
				font-size: 0.75rem;
				white-space: nowrap;
				opacity: 0;
				transition: all 0.2s;
				pointer-events: none;
				z-index: 1000;
			}
		}

		.chat {
			flex: 1;
			background: rgba(255, 255, 255, 0.1);
			border: 1px solid rgba(255, 255, 255, 0.2);
			border-radius: 20px;
			padding: 0.5rem 1rem;
			color: white;
			font-size: 0.9rem;
			outline: none;
			transition: border-color 0.2s;

			&:focus {
				border-color: #3280d3;
			}

			&::placeholder {
				color: rgba(255, 255, 255, 0.5);
			}
		}

		.send {
			background: #3280d3;
			border: none;
			border-radius: 50%;
			width: 40px;
			height: 40px;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			transition: background-color 0.2s;

			&:hover:not(:disabled) {
				background: #2a6bb3;
			}

			&:disabled {
				opacity: 0.5;
				cursor: not-allowed;
			}

			svg {
				fill: white;
			}
		}

		.suggestion {
			position: absolute;
			left: 0;
			right: 0;
			background: #1a2f49;
			border: 1px solid rgba(255, 255, 255, 0.2);
			border-radius: 8px;
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
			color: white;
			z-index: 1000;
			overflow: hidden;

			> div {
				padding: 0.5rem;
				background: rgba(255, 255, 255, 0.1);
				font-size: 0.75rem;
				text-align: center;
				border-bottom: 1px solid rgba(255, 255, 255, 0.1);
			}

			ul {
				list-style: none;
				margin: 0;
				padding: 0;
				max-height: 100px;
				overflow-y: auto;

				li {
					padding: 0.5rem;
					cursor: pointer;
					transition: background-color 0.2s;

					&:hover,
					&:focus {
						background: rgba(255, 255, 255, 0.1);
						outline: none;
					}
				}
			}
		}
	}
}
</style>
